import {
  type DeadlineRenewalKind,
  isRecurringDeadline,
  type RenewDeadlineFormDto,
} from '@prospero/shared';
import type { RenewDeadlineTarget } from './RenewDeadlineDialog';
import type { RenewDeadlineVariables } from './use-deadline-mutations';

/**
 * "Carica il documento dopo": unica senza file nel ciclo corrente e con data non passata
 * (creata senza file). Il dialog precompila la data della riga: il backend
 * accetta una nuova data uguale a quella attuale.
 */
type AttachCandidate = Pick<RenewDeadlineTarget, 'validityMonths' | 'dueAt' | 'hasCurrentFile'>;

export function isAttachOnlyRenewal(target: AttachCandidate, todayIso: string): boolean {
  return (
    !isRecurringDeadline(target) &&
    !target.hasCurrentFile &&
    target.dueAt !== null &&
    target.dueAt >= todayIso
  );
}

/** "Nessuna scadenza" (es. DVR): il file si carica e la riga resta senza data, nessuna data inventata. */
export function isAttachWithoutDateRenewal(target: AttachCandidate): boolean {
  return !isRecurringDeadline(target) && target.dueAt === null;
}

/** Il dialog si apre come "Carica il documento": stessa dicitura va sulla voce di menu. */
export function isAttachRenewal(target: AttachCandidate, todayIso: string): boolean {
  return isAttachOnlyRenewal(target, todayIso) || isAttachWithoutDateRenewal(target);
}

export interface RenewMode {
  kind: DeadlineRenewalKind;
  /** Il tenant su una riga del consulente non sceglie né persona né validità. */
  isRestricted: boolean;
  canBecomeRecurring: boolean;
  isAttachOnly: boolean;
  isAttachWithoutDate: boolean;
}

export function resolveRenewMode(
  target: RenewDeadlineTarget | null,
  hasCustomMutation: boolean,
  todayIso: string,
): RenewMode {
  const isRestricted = target?.isManagedByConsultant === true && !hasCustomMutation;
  const kind: DeadlineRenewalKind = target && isRecurringDeadline(target) ? 'RECURRING' : 'ONE_OFF';
  const isAttachOnly = target !== null && isAttachOnlyRenewal(target, todayIso);
  const isAttachWithoutDate = target !== null && isAttachWithoutDateRenewal(target);
  return {
    kind,
    isRestricted,
    // Allegare il file con la data già nota non è una decisione sulla ricorrenza (resta in "Modifica").
    canBecomeRecurring:
      kind === 'ONE_OFF' && !isRestricted && !isAttachWithoutDate && !isAttachOnly,
    isAttachOnly,
    isAttachWithoutDate,
  };
}

/** Con la data già nota si sta solo allegando il file: la lettura IA è un'alternativa, non una scelta alla pari. */
export function resolveAiButtonVariant(mode: RenewMode): 'primary' | 'secondary' {
  return mode.isAttachOnly || mode.isAttachWithoutDate ? 'secondary' : 'primary';
}

export function buildRenewDefaults(
  target: RenewDeadlineTarget | null,
  mode: RenewMode,
  todayIso: string,
): RenewDeadlineFormDto {
  return {
    renewalKind: mode.kind,
    issuedAt: mode.kind === 'RECURRING' ? todayIso : undefined,
    dueAt: mode.isAttachOnly ? (target?.dueAt ?? undefined) : undefined,
    dueAtRequired: !mode.isAttachWithoutDate,
    becomesRecurring: false,
    validityMonths: target?.validityMonths ?? undefined,
    file: null,
    assigneeUserId: target?.assignee?.userId ?? null,
    requestClientSignature: target?.rowRequestsClientSignature,
  };
}

/** La firma del singolo file viaggia solo dal consulente che gestisce la riga (lo switch c'è solo per lui). */
function signatureVariables(
  target: RenewDeadlineTarget,
  values: RenewDeadlineFormDto,
): Pick<RenewDeadlineVariables, 'requestClientSignature'> {
  if (target.rowRequestsClientSignature === undefined) {
    return {};
  }
  return {
    requestClientSignature: values.requestClientSignature ?? target.rowRequestsClientSignature,
  };
}

function renewDateVariables(mode: RenewMode, values: RenewDeadlineFormDto) {
  if (mode.kind === 'RECURRING') {
    return { issuedAt: values.issuedAt };
  }
  return mode.isAttachWithoutDate ? {} : { dueAt: values.dueAt };
}

interface RenewVariablesInput {
  target: RenewDeadlineTarget;
  mode: RenewMode;
  values: RenewDeadlineFormDto;
  file: File;
}

export function buildRenewVariables({
  target,
  mode,
  values,
  file,
}: RenewVariablesInput): RenewDeadlineVariables {
  const sendsAssignee = target.isPersonal && !mode.isRestricted;
  const sendsValidity =
    !mode.isRestricted &&
    values.validityMonths !== undefined &&
    (mode.kind === 'RECURRING' || values.becomesRecurring);
  return {
    deadlineId: target.id,
    file,
    ...renewDateVariables(mode, values),
    ...((mode.isAttachOnly || mode.isAttachWithoutDate) && { attachOnly: true }),
    ...(sendsValidity && { validityMonths: values.validityMonths }),
    ...(sendsAssignee && { assigneeUserId: values.assigneeUserId ?? null }),
    ...signatureVariables(target, values),
  };
}

/** Il documento è già in deposito temporaneo: al server viaggia il token, non il file. */
export function applyUploadToken(
  variables: RenewDeadlineVariables,
  uploadToken: string,
): RenewDeadlineVariables {
  const { file: _alreadyUploaded, ...withoutFile } = variables;
  return { ...withoutFile, uploadToken };
}
